import {
  AppstoreOutlined,
  MenuOutlined,
  RobotOutlined,
  SafetyOutlined,
  SettingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { Button, Divider, Drawer, Dropdown, Grid, Layout, Select, Space } from 'antd';
import { useState } from 'react';
import { Logo } from '@/components/Logo';
import { useAuth } from '@/features/auth/auth-context';
import { NotificationBell } from '@/features/notifications/ui/NotificationBell';
import { InstallButton } from '@/features/pwa/InstallButton';
import { ActiveOrgProvider } from '@/features/organizations/ActiveOrgProvider';
import { OrgSwitcher } from '@/features/organizations/ui/OrgSwitcher';
import { useI18n } from '@/i18n/i18n';
import { RequireAuth } from './guards';

const { Header, Content } = Layout;

function Shell() {
  const { user, signOut } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  // `md` is only ever undefined during the first render; treat that as desktop.
  const isMobile = screens.md === false;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const onSignOut = () => {
    setDrawerOpen(false);
    void signOut().then(() => navigate({ to: '/login' }));
  };

  const langSelect = (
    <Select
      size="small"
      value={lang}
      onChange={(v) => setLang(v)}
      aria-label={t('common.language')}
      style={{ width: 90 }}
      options={[
        { value: 'en', label: 'EN' },
        { value: 'ar', label: 'ع' },
      ]}
    />
  );

  const accountItems = [
    {
      key: 'members',
      label: <Link to="/members">{t('nav.members')}</Link>,
      icon: <TeamOutlined />,
    },
    {
      key: 'org-settings',
      label: <Link to="/settings/organization">{t('nav.orgSettings')}</Link>,
      icon: <SettingOutlined />,
    },
    {
      key: 'ai',
      label: <Link to="/settings/ai">{t('nav.aiSettings')}</Link>,
      icon: <RobotOutlined />,
    },
    {
      key: 'security',
      label: <Link to="/settings/security">{t('nav.security')}</Link>,
      icon: <SafetyOutlined />,
    },
    { type: 'divider' as const },
    { key: 'signout', label: t('nav.signOut'), danger: true, onClick: onSignOut },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: isMobile ? '0 16px' : undefined,
        }}
      >
        <Link to="/" style={{ color: '#fff', display: 'inline-flex', alignItems: 'center' }}>
          <Logo size={28} markOnly={isMobile} />
        </Link>

        {isMobile ? (
          <Space size="small">
            <NotificationBell />
            <Button
              type="text"
              icon={<MenuOutlined />}
              style={{ color: '#fff' }}
              aria-label={t('a11y.account')}
              onClick={() => setDrawerOpen(true)}
            />
          </Space>
        ) : (
          <Space size="middle">
            <Link to="/projects" style={{ color: '#fff' }} aria-label={t('nav.projects')}>
              <Space size={6}>
                <AppstoreOutlined />
                {t('nav.projects')}
              </Space>
            </Link>
            <Link to="/agents" style={{ color: '#fff' }} aria-label={t('nav.agents')}>
              <Space size={6}>
                <ThunderboltOutlined />
                {t('nav.agents')}
              </Space>
            </Link>
            <OrgSwitcher />
            {langSelect}
            <InstallButton />
            <NotificationBell />
            <Dropdown menu={{ items: accountItems }}>
              <Button type="text" style={{ color: '#fff' }} aria-label={t('a11y.account')}>
                {user?.email ?? 'Account'}
              </Button>
            </Dropdown>
          </Space>
        )}
      </Header>

      <Drawer
        placement={lang === 'ar' ? 'left' : 'right'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={user?.email ?? 'Account'}
        styles={{ body: { padding: 16 } }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Link to="/projects" onClick={() => setDrawerOpen(false)}>
            <Space size={8}>
              <AppstoreOutlined />
              {t('nav.projects')}
            </Space>
          </Link>
          <Link to="/agents" onClick={() => setDrawerOpen(false)}>
            <Space size={8}>
              <ThunderboltOutlined />
              {t('nav.agents')}
            </Space>
          </Link>
          <Link to="/members" onClick={() => setDrawerOpen(false)}>
            <Space size={8}>
              <TeamOutlined />
              {t('nav.members')}
            </Space>
          </Link>
          <Link to="/settings/organization" onClick={() => setDrawerOpen(false)}>
            <Space size={8}>
              <SettingOutlined />
              {t('nav.orgSettings')}
            </Space>
          </Link>
          <Link to="/settings/ai" onClick={() => setDrawerOpen(false)}>
            <Space size={8}>
              <RobotOutlined />
              {t('nav.aiSettings')}
            </Space>
          </Link>
          <Link to="/settings/security" onClick={() => setDrawerOpen(false)}>
            <Space size={8}>
              <SafetyOutlined />
              {t('nav.security')}
            </Space>
          </Link>
          <Divider style={{ margin: '4px 0' }} />
          <div>
            <OrgSwitcher />
          </div>
          <Space size="middle">
            {langSelect}
            <InstallButton />
          </Space>
          <Button danger block onClick={onSignOut}>
            {t('nav.signOut')}
          </Button>
        </Space>
      </Drawer>

      <Content
        style={{
          padding: isMobile ? 16 : 24,
          maxWidth: 960,
          width: '100%',
          margin: '0 auto',
        }}
      >
        <Outlet />
      </Content>
    </Layout>
  );
}

/** Protected application shell — guards, loads org context, then renders content. */
export function AuthenticatedLayout() {
  return (
    <RequireAuth>
      <ActiveOrgProvider>
        <Shell />
      </ActiveOrgProvider>
    </RequireAuth>
  );
}
